import { GridColumn } from "@glideapps/glide-data-grid";
import { IAddRows } from "../../Components/CargaPorTabla/InterfaceTabla";

/**
 * Procesa los valores pegados para transformarlos en una estructura de datos.
 * * @param target [columna, fila] de inicio del pegado.
 * @param values Matriz (array de arrays) de valores pegados.
 * @param getCellContent Función para obtener el contenido de la celda.
 * @param columns Array de definiciones de columna.
 * @param addRows Array de datos de filas adicionales (para obtener el ID).
 * @returns Matriz de datos procesados (pastedData).
 */
export const processPastedValues = (
    target,
    values,
    getCellContent,
    columns,
    addRows,
    rowIndexes?: number[]
) => {
    const [col, row] = target;
    const pastedData:any[] = [];

    values.forEach((rowVals, dr) => {
        const rowArr: any[] = [];
        let rowIdAdded = false;

        rowVals.forEach((val, dc) => {
            const cc = col + dc;
            const colDef = columns[cc];
            const id = addRows[rowIndexes ? rowIndexes[dr] : row + dr]?.id;
            let value = val?.toString().trim();

            if (value) {
              value = value
                .replace(/[\n\r]+/g, " ") // Reemplaza saltos de línea por espacio
                .replace(/[^\S\r\n]+/g, " ") // Normaliza espacios múltiples
                .trim(); // Quita espacios al inicio y final;
            }

            if (!rowIdAdded) {
                rowArr.push({ id });
                rowIdAdded = true;
            }

            if (colDef && colDef.id) {
                rowArr.push({ [colDef.id]: value });
            }
        });

        pastedData.push(rowArr);
    });

    return pastedData;
};

const toIndexArray = (sel: any): number[] => {
    if (Array.isArray(sel)) return sel;
    if (sel && typeof sel.toArray === "function") return sel.toArray();
    return [];
};

const repeatTimes = (selectedSize: number, sourceSize: number) =>
    selectedSize > sourceSize && selectedSize % sourceSize === 0
        ? selectedSize / sourceSize
        : 1;

/**
 * Expande los valores copiados sobre la selección actual, igual que Excel:
 * si el área seleccionada es múltiplo del área copiada, los valores se repiten
 * hasta llenarla (ej. 1 fila copiada + 5 filas seleccionadas = 5 filas pegadas);
 * si no es múltiplo, se pega una sola vez desde la celda de inicio.
 * * @param target [columna, fila] de inicio del pegado.
 * @param values Matriz de valores copiados.
 * @param selection Selección actual de la grilla (GridSelection).
 * @param totalRows Cantidad de filas de la grilla.
 * @returns target ajustado, matriz expandida y los índices de fila destino de cada fila de la matriz.
 */
export const expandPastedValuesToSelection = (
    target: readonly [number, number],
    values: string[][],
    selection: any,
    totalRows: number
) => {
    // Excel agrega un salto de línea al final del texto copiado
    const source = values.length > 1 && values[values.length - 1].every(v => v === "")
        ? values.slice(0, -1)
        : values;

    const sourceHeight = source.length;
    const sourceWidth = Math.max(1, ...source.map(r => r.length));

    let [col, row] = target;
    let selectedWidth = 1;
    let selectedRowIndexes: number[] = [];

    const range = selection?.current?.range;
    const rowsSel = toIndexArray(selection?.rows).sort((a, b) => a - b);

    if (range && (range.height > 1 || range.width > 1)) {
        // 1) Rango de celdas seleccionado
        col = range.x;
        row = range.y;
        selectedWidth = range.width;
        for (let r = range.y; r < range.y + range.height; r++) selectedRowIndexes.push(r);
    } else if (rowsSel.length > 1) {
        // 2) Filas completas seleccionadas (pueden no ser contiguas)
        row = rowsSel[0];
        selectedRowIndexes = rowsSel;
    }

    const rowTimes = repeatTimes(selectedRowIndexes.length, sourceHeight);
    const colTimes = repeatTimes(selectedWidth, sourceWidth);

    const rowIndexes = rowTimes > 1
        ? selectedRowIndexes
        : Array.from({ length: sourceHeight }, (_, i) => row + i);

    const expandedValues = rowIndexes.map((_, i) => {
        const rowVals = source[i % sourceHeight];
        return Array.from({ length: rowVals.length * colTimes }, (_, c) => rowVals[c % rowVals.length]);
    });

    const validRows = rowIndexes
        .map((r, i) => ({ r, vals: expandedValues[i] }))
        .filter(({ r }) => r >= 0 && r < totalRows);

    return {
        target: [col, row] as [number, number],
        values: validRows.map(v => v.vals),
        rowIndexes: validRows.map(v => v.r),
    };
};

/**
 * Retorna una función de manejo de redimensionamiento de columnas (handleColumnResize).
 * Esta función se debe usar con React.useCallback en el componente.
 * * @param setColumns Función de setState de React para actualizar el array de columnas.
 * @returns Función que maneja el evento onColumnResize.
 */
export const handleColumnResizeUtil = (
  setColumns: React.Dispatch<React.SetStateAction<GridColumn[]>>
) => {
  return (column: GridColumn, newSize: number, colIndex: number, newSizeWithGrow: number) => {
    setColumns(prevCols => {
      if (colIndex < 0 || colIndex >= prevCols.length) return prevCols;
      const newCols = prevCols.slice();
      newCols[colIndex] = { ...newCols[colIndex], width: newSize };
      return newCols;
    });
  };
};

export const saveOnSessionStorageDataTable = (data: any[]) => {
  sessionStorage.setItem('dataTable', JSON.stringify(data));
};

export const getSessionStorageDataTable = (): IAddRows[] => {
  const stored = sessionStorage.getItem("dataTable");
  if (!stored) return [];

  try {
    return JSON.parse(stored);
  } catch (e) {
    console.warn("Error al parsear dataTable desde sessionStorage:", e);
    return [];
  }
};
